<template>
  <v-input>
    <div class="flex-1-1">
      <FieldLabel
        v-if="!readonly"
        class="ml-4"
        :field="field"
      />

      <div
        :id="editorId"
        ref="editorRef"
        class="rich-text-editor"
      />
    </div>
  </v-input>
</template>

<script setup lang="ts">
import useUploadMedia from '~/composables/useUploadMedia';
import useMediaAssetUrl from '~/composables/useMediaAssetUrl';
import { useToastAlerts } from '~/stores/toastAlerts.store';

const props = defineProps<{
  field: FieldConfig
  readonly: boolean
  errors?: string[]
  dense?: boolean
  rules?: FormRule[]
}>();

const { $quill } = useNuxtApp();
const api = useApi();
let quillEditor: any;

const model = defineModel<string | string[]>();
const toastAlerts = useToastAlerts();
const editorId = computed(() => `editor__${props.field.id}__${props.readonly}`);
const editorRef = ref();

// Configure the Quill toolbar options
const standardToolbar = [
  [{ header: [1, 2, 3, false] }],
  ['bold', 'italic', 'underline', 'link'],
  [{ list: 'ordered' }, { list: 'bullet' }],
  [{ indent: '-1' }, { indent: '+1' }],
  ['image'],
  ['clean'],
];

// Custom renderer to turn the MediaAsset inserts into rendered img,
// with the catch that this has to go through the async useMediaAssetUrl.
const resolveBlotImageSrcs = async (container: HTMLElement) => {
  const imgNodes = container.querySelectorAll('img[data-resolve-src]');
  for (const node of imgNodes) {
    const raw = node.getAttribute('data-asset');
    if (!raw) {
      continue;
    }

    try {
      const asset = JSON.parse(raw);
      const resolvedUrl = await useMediaAssetUrl(asset);
      node.setAttribute('src', resolvedUrl);
      node.removeAttribute('data-resolve-src');
    }
    catch (err) {
      console.error('Failed to load media asset:', err);
      node.setAttribute('src', '');
    }
  }
};

/**
 *  Create the image handler, attached to the toolbar, but handling the image processing.
 * This has to
 */
const customImageHandler = async () => {
  if (!quillEditor) {
    toastAlerts.error('Editor not available.');
    return;
  }

  quillEditor.focus();
  const input = document.createElement('input');
  input.setAttribute('type', 'file');
  input.setAttribute('accept', 'image/*');
  input.style.display = 'none';

  input.onchange = async () => {
    if (!input.files?.length) {
      document.body.removeChild(input);
      return;
    }
    const files = Array.from(input.files);
    document.body.removeChild(input);

    // After receiving the file, create the 'insert' for Quill that holds the file
    try {
      const insertIndex = quillEditor.getSelection()?.index ?? quillEditor.getLength();
      const uploadedAssets = await useUploadMedia(files);

      uploadedAssets.forEach(async (asset: MediaAsset) => {
        const imageUrl = await useMediaAssetUrl(asset);
        if (imageUrl) {
          asset.asset_file = imageUrl;
          quillEditor.insertEmbed(insertIndex, 'mediaasset', asset, $quill.sources.USER);
          setTimeout(() => {
            quillEditor.setSelection(insertIndex + 1, 0, $quill.sources.SILENT);
          }, 0);
          await nextTick(); // ensure Quill rendered
          await resolveBlotImageSrcs(editorRef.value);

          // Update the title on the image
          const updateAsset = asset as Partial<MediaAsset>;
          delete updateAsset.asset_file;
          updateAsset.title = `${props.field.label}_${insertIndex}`;
          await api.updateMediaAsset(asset.id, updateAsset);
        }
        else {
          toastAlerts.warning(`Could not get URL for uploaded image ${asset.id}`);
        }
      });
    }
    catch (error) {
      console.error('Error uploading image:', error);
      toastAlerts.error('Failed to upload image.');
    }
  };

  document.body.appendChild(input);
  input.click();
};

onMounted(async () => {
  const ImageBlot = $quill.import('formats/image');

  class MediaAssetBlot extends ImageBlot {
    static blotName = 'mediaasset';
    static tagName = 'img';

    static create(value: MediaAsset) {
      const node = super.create(''); // useMediaAssetUrl is async now
      node.setAttribute('id', value.id.toString());
      node.setAttribute('data-asset', JSON.stringify(value));
      node.setAttribute('src', '');
      node.setAttribute('data-resolve-src', 'true');
      return node;
    }

    static value(node: HTMLElement): MediaAsset {
      return {
        id: Number.parseInt(node.getAttribute('id') || '0', 10),
        asset_file: node.getAttribute('src') || '',
        title: '',
        description: '',
        asset_type: 'image',
      };
    }
  }

  $quill.register(MediaAssetBlot);

  // Initialize Quill editor
  quillEditor = new $quill(editorRef.value, {
    modules: {
      toolbar: props.readonly
        ? false
        : {
          container: standardToolbar,
          handlers: {
            image: customImageHandler,
          },
        },
    },
    theme: 'snow',
    readOnly: props.readonly,
  });

  // Set initial content if available
  if (model.value) {
    try {
      quillEditor.setContents(JSON.parse(model.value as string));

      await nextTick(); // ensure Quill rendered
      await resolveBlotImageSrcs(editorRef.value);
    }
    catch (e) {
      console.log(`Existing ${props.field.id} is not formatted correctly`);
    }
  }

  // Listen for changes
  quillEditor.on('text-change', async () => {
    model.value = JSON.stringify(quillEditor.getContents().ops);
    await nextTick();
    await resolveBlotImageSrcs(editorRef.value);
  });

  quillEditor.enable(!props.readonly);
},
);

// Watch for changes in model value or readonly props
watch(() => model.value, () => {
  if (props.readonly && quillEditor) {
    try {
      quillEditor.setContents(JSON.parse(model.value as string));
    }
    catch (e) {
      console.error('Error updating Quill content', e);
    }
  }
});

watch(() => props.readonly, newVal => {
  if (quillEditor) {
    quillEditor.enable(!newVal);
  }
});
</script>

<style scoped>
@import 'https://cdn.jsdelivr.net/npm/quill@2.0.3/dist/quill.snow.css';

.rich-text-editor {
  min-height: 200px;
}
</style>
